import { useState, useEffect } from "react";
import { FiX, FiSearch } from "react-icons/fi";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Who We Are", href: "#about" },
    { name: "What We Do", href: "#projects" },
    { name: "Contact Us", href: "#contact" },
    { name: "Blog", href: "#" },
  ];

  const utilityLinks = ["Careers", "Media Centre", "Sustainability", "Investor Relations", "PGV Privilege"];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

        .pgv-navbar {
          font-family: 'Montserrat', sans-serif;
        }
      `}</style>

      <nav
        className={`pgv-navbar fixed w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-black/90 backdrop-blur-md border-b border-white/10 shadow-2xl"
            : "bg-transparent"
        }`}
      >
        {/* Top Utility Bar - Hidden on mobile */}
        {/* <div className={`hidden lg:flex justify-end items-center px-8 py-2 border-b border-white/10 transition-all duration-300 ${scrolled ? 'opacity-80' : 'opacity-100'}`}>
          {utilityLinks.map((link) => (
            <a
              key={link}
              href="#"
              className="text-white/50 hover:text-gold text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 px-3"
            >
              {link}
            </a>
          ))}
        </div> */}

        {/* Main Navbar */}
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
          <div className="flex justify-between items-center h-[70px] lg:h-[80px]">
            {/* Logo - Purvanchal Global Ventures */}
            <a href="#home" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-16 h-16 lg:w-20 lg:h-20 border border-gold flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-1 border border-gold/30"></div>
                  <img 
                    src="/logo.png" 
                    alt="Purvanchal Global Ventures Logo" 
                    className="w-full h-full object-contain z-10"
                  />
                </div>
              </div>
              <div className="hidden sm:block">
                <div className="font-montserrat text-white text-[11px] lg:text-xs tracking-[0.3em] uppercase leading-tight">
                  PURVANCHAL
                </div>
                <div className="font-serif text-gold text-[8px] lg:text-[9px] tracking-[0.25em] uppercase mt-0.5">
                  Global Ventures
                </div>
              </div>
            </a>

            {/* Desktop Navigation Links */}
            <ul className="hidden lg:flex items-center gap-8 xl:gap-10">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="relative text-white/80 hover:text-white text-[11px] font-medium tracking-[0.14em] uppercase transition-colors duration-300 pb-1 group"
                  >
                    {link.name}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-gold group-hover:w-full transition-all duration-300"></span>
                  </a>
                </li>
              ))}
            </ul>

            {/* Right Section - Search + Menu Button */}
            <div className="flex items-center gap-4 lg:gap-6">
              {/* Search Icon */}
              <button
                className="text-white/70 hover:text-gold transition-colors duration-300 p-1"
                aria-label="Search"
              >
                <FiSearch size={18} />
              </button>

              {/* Divider - Desktop only */}
              <div className="hidden lg:block w-px h-5 bg-white/20"></div>

              {/* Desktop CTA Button */}
              <a href="#projects" className="hidden lg:block bg-gold text-white text-[10px] font-medium tracking-[0.22em] uppercase px-6 py-2.5 hover:bg-gold-dark transition-all duration-300 hover:translate-y-[-1px]">
                Explore Projects
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setOpen(!open)}
                className="lg:hidden flex flex-col items-center justify-center gap-1.5 p-1"
                aria-label="Toggle menu"
              >
                {open ? (
                  <FiX size={24} className="text-white" />
                ) : (
                  <>
                    <span className="w-5 h-px bg-white/80 transition-all"></span>
                    <span className="w-3 h-px bg-white/80 transition-all"></span>
                    <span className="w-4 h-px bg-white/80 transition-all"></span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay - Luxury Style */}
        <div
          className={`fixed inset-0 z-50 lg:hidden transition-all duration-500 ease-in-out ${
            open ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/95 backdrop-blur-xl"
            onClick={() => setOpen(false)}
          ></div>

          {/* Menu Content */}
          <div className="relative h-full flex flex-col justify-center items-center px-8 py-20">
            {/* Close Button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-6 right-6 text-white/60 hover:text-gold text-3xl transition-colors"
            >
              <FiX size={28} />
            </button>

            {/* Logo */}
            <div className="flex flex-col items-center mb-12">
              <div className="w-16 h-16 border border-gold flex items-center justify-center relative mb-3 overflow-hidden">
                <div className="absolute inset-1.5 border border-gold/30"></div>
                <img 
                  src="/logo.png" 
                  alt="Purvanchal Global Ventures Logo" 
                  className="w-full h-full object-contain z-10"
                />
              </div>
              <div className="text-center">
                <div className="font-montserrat text-white text-xs tracking-[0.35em] uppercase">
                  PURVANCHAL
                </div>
                <div className="font-serif text-gold text-[10px] tracking-[0.3em] uppercase mt-1">
                  Global Ventures
                </div>
              </div>
            </div>

            {/* Navigation Links */}
            <ul className="flex flex-col items-center gap-6 mb-12">
              {navLinks.map((link, index) => (
                <li
                  key={link.name}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  className={`transform transition-all duration-500 ${
                    open ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                  }`}
                >
                  <a
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className="font-serif text-white/80 hover:text-gold text-3xl lg:text-4xl font-light tracking-wide transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>

            {/* Utility Links */}
            <div
              className={`flex flex-wrap justify-center gap-x-6 gap-y-3 max-w-md transform transition-all duration-500 delay-300 ${
                open ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
            >
              {utilityLinks.map((link) => (
                <a
                  key={link}
                  href="#"
                  onClick={() => setOpen(false)}
                  className="text-white/40 hover:text-gold text-[9px] tracking-[0.16em] uppercase transition-colors"
                >
                  {link}
                </a>
              ))}
            </div>

            {/* Mobile CTA */}
            <a
              href="#projects"
              onClick={() => setOpen(false)}
              className={`mt-10 bg-gold text-white text-[10px] font-medium tracking-[0.22em] uppercase px-8 py-3 hover:bg-gold-dark transition-all duration-300 transform ${
                open ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
            >
              Explore Projects
            </a>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content jump */}
      <div className="h-[70px] lg:h-[80px]"></div>
    </>
  );
};
export default Navbar;